import {
  chakra,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  StackDivider,
  Text,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { Card, CardContent } from 'components/cards/Card';
import { FormQuestionType, QuestionIcon } from 'components/icons/QuestionIcon';
import { OverlayProps } from 'components/TaskOverlay';
import React from 'react';
import { CreateQuestionInput } from '../..';
import QUESTION_DATABASE from './questionDatabase';

interface QuestionDatabaseModalProps extends OverlayProps {
  onSelectQuestion(question: CreateQuestionInput): void;
}

export const QuestionDatabaseModal = ({
  isOpen,
  onClose,
  onSelectQuestion,
}: QuestionDatabaseModalProps) => {
  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t`Predefined questions`}</ModalHeader>
          <ModalBody>
            <Stack>
              {QUESTION_DATABASE().map((question, index) => (
                <PredefinedQuestion
                  key={index}
                  question={question}
                  onClick={() => {
                    onSelectQuestion(question);
                  }}
                />
              ))}
            </Stack>
          </ModalBody>
          <ModalFooter />
        </ModalContent>
      </Modal>
    </>
  );
};

interface PredefinedQuestionProps {
  question: CreateQuestionInput;
  onClick(): void;
}

const PredefinedQuestion = ({ question, onClick }: PredefinedQuestionProps) => {
  const { text } = question;

  const type = createQuestionInputToType(question);

  if (!type) {
    return null;
  }

  return (
    <Card onClick={onClick}>
      <CardContent>
        <HStack divider={<StackDivider />}>
          <QuestionIcon questionType={type} />
          <Text>
            {text} <QuestionSubtext question={question} />
          </Text>
        </HStack>
      </CardContent>
    </Card>
  );
};

const QuestionSubtext = ({ question }: { question: CreateQuestionInput }) => {
  const { checkboxesDefinition, numericDefinition } = question;

  let subtext;

  if (checkboxesDefinition) {
    const amountOfOptions = checkboxesDefinition.options.length;
    subtext = t`${amountOfOptions} options`;
  } else if (numericDefinition) {
    subtext = t`Range from ${numericDefinition.minValue} to ${numericDefinition.maxValue}`;
  }

  if (!subtext) {
    return null;
  }

  return (
    <chakra.span fontSize="sm" color="gray.500">{`(${subtext})`}</chakra.span>
  );
};

function createQuestionInputToType(
  input: CreateQuestionInput,
): FormQuestionType | undefined {
  const { stringDefinition, checkboxesDefinition, numericDefinition } = input;

  if (!!stringDefinition) {
    return FormQuestionType.TEXT;
  } else if (!!checkboxesDefinition) {
    return FormQuestionType.CHECKBOXES;
  } else if (!!numericDefinition) {
    return FormQuestionType.NUMERIC;
  }
}
